import { createClient } from '@supabase/supabase-js'
import { DATABASE_CONFIG } from '@/config/database.config'
import { EDGE_FUNCTIONS_CONFIG } from '@/config/edge-functions.config'
import { STRIPE_CONFIG } from '@/config/stripe.config'
import type { Database } from '@/types/database.types'

// Initialize and validate configuration (logs on import)
DATABASE_CONFIG.validate()
const dbConfig = DATABASE_CONFIG.getSupabaseConfig()
EDGE_FUNCTIONS_CONFIG.validate()
void EDGE_FUNCTIONS_CONFIG.getEdgeFunctionsConfig()
STRIPE_CONFIG.validate()
void STRIPE_CONFIG.getStripeConfig()

// Helpers
const deriveProjectRef = (url: string): string => {
  try {
    const host = new URL(url).host
    // prod: <ref>.supabase.co / .supabase.in ; local: 127.0.0.1
    const maybeRef = host.split('.')[0]
    return maybeRef || 'local'
  } catch {
    return 'local'
  }
}

const projectRef = deriveProjectRef(dbConfig.url)
// Use a clean storage key so Supabase writes predictable keys: sb-${storageKey}-auth-token
const storageKey = `etb-${projectRef}`
const envTag = DATABASE_CONFIG.isUsingLocalDatabase() ? 'local' : 'prod'

const matchesSupabaseAuthKey = (key: string) =>
  /^(sb-|etb-).*-auth-token/.test(key) || /^(sb-|etb-).*-auth-token-code-verifier/.test(key)

const isCurrentEnvKey = (key: string) => key.includes(projectRef) || key.includes(storageKey)

export const pruneCrossEnvAuthData = () => {
  try {
    const keys = Object.keys(localStorage)
    keys.forEach((key) => {
      if (matchesSupabaseAuthKey(key) && !isCurrentEnvKey(key)) {
        localStorage.removeItem(key)
      }
    })
  } catch (e) {
    console.warn('[Supabase] Error pruning auth data:', e)
  }
}

export const clearAllAuthData = () => {
  try {
    const keys = Object.keys(localStorage)
    keys.forEach((key) => {
      if (matchesSupabaseAuthKey(key)) {
        localStorage.removeItem(key)
      }
    })
    localStorage.removeItem('exercise-toolbox-auth')
  } catch (e) {
    console.warn('[Supabase] Error clearing auth data:', e)
  }
}

// One-time startup hygiene: drop tokens from other envs to prevent ghost sessions
pruneCrossEnvAuthData()

// Create client with env-scoped storage key to prevent cross-env bleed
export const supabase = createClient<Database>(dbConfig.url, dbConfig.anonKey, {
  auth: {
    storageKey,
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
})

export const isLocalDatabase = () => DATABASE_CONFIG.isUsingLocalDatabase()
export const isLocalEdgeFunctions = () => EDGE_FUNCTIONS_CONFIG.isUsingLocalEdgeFunctions()

const withTimeout = async <T>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
  return await Promise.race([
    promise,
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error(`${label}_TIMEOUT`)), ms)),
  ])
}

export const signOutSafely = async () => {
  console.log('[Supabase] signOutSafely start')
  clearAllAuthData()

  try {
    await withTimeout(supabase.auth.signOut({ scope: 'global' }), 2000, 'SIGNOUT')
    console.log('[Supabase] signOutSafely complete')
  } catch (err) {
    console.warn('[Supabase] signOutSafely timeout/err (local cleared):', err)
  }
}

export const fetchProfileWithTimeout = async (userId: string) => {
  const query = supabase.from('users').select('*').eq('id', userId).single()
  return await withTimeout(
    Promise.resolve(query),
    8000, // Increased from 3s - profile fetch can be slow on cold starts
    'PROFILE'
  )
}

export const getSessionWithTimeout = async () => {
  return await withTimeout(supabase.auth.getSession(), 8000, 'SESSION') // Increased from 3s
}

export { DATABASE_CONFIG, EDGE_FUNCTIONS_CONFIG, envTag, storageKey, projectRef }
